import { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { FieldArray } from 'formik';
import isEqual from 'lodash/isEqual';
import clsx from 'clsx';
import * as yup from 'yup';
import { makeStyles, Grid, Typography, Button, Table, TableBody, TableRow, TableCell, Box } from '@material-ui/core';
import PeopleAltRoundedIcon from '@material-ui/icons/PeopleAltRounded';
import RefreshOutlinedIcon from '@material-ui/icons/RefreshOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { useAppSelector, useAppDispatch } from '../../../store';
import { AlertMessage, Colors, TitleText, DialogBox, SubtitleText, FormLabel, ErrorMessage } from '../../controls/helpers';
import { confirmClientAsync, getApplicationPdf, getClientAsync, getSavingsAdviceToolPdf } from '../../../store/adviser';
import { ConfirmationType, ConfirmClientCommand, DistributionOptions, InvestmentOptions, Product } from '../../../api';
import { Details, Section, DetailText } from '../Section';
import { applicationNeedsConfirmation, ExtendedApplicationAssetAllocation, formatDate, getAllProductApplications } from '../Helpers';
import { investmentOptionNames, investmentOptionOptions, productDetails } from '../../../common/types';
import { NumberTextInput, RadioList, TextAreaInput } from '../../controls/inputs';
import { getAsync } from '../../../store/clientQuestionnaires';
import FormikWrapper from '../../containers/FormikWrapper';
import { FUNDS } from '../../controls/inputs/InvestmentOption';

enum FormMode {
  None,
  Confirming,
  Confirmed,
  AlertNoChanges
}

const AdviceConfirmation = ({ history, match }: RouteComponentProps) => {
  const { client, confirmClient, isSubmitting, errors } = useAppSelector(state => state.adviser);
  const { responses } = useAppSelector(state => state.clientQuestionnaires);
  const [formMode, setFormMode] = useState<FormMode>(FormMode.None);
  const clientId = (match.params as { clientId: string }).clientId;
  const allProductAllocations = getAllProductApplications(client!);
  const ksAllocations = allProductAllocations.find(data => data.product === Product.KiwiSaver);
  const wbAllocations = allProductAllocations.filter(data => data.product === Product.WealthBuilder);
  const incomeGeneratorApplications = allProductAllocations.filter(data => data.product === Product.IncomeGenerator);
  const primaryAllocations = allProductAllocations.filter(data => data.active);
  const createdDate = formatDate(client?.created);
  const completedDate = formatDate(client?.applicationCompleted);
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const handleSubmit = (values: ConfirmClientCommand) => {
    const _values = {
      ...values,
      applicationAssetAllocations: values.applicationAssetAllocations?.map(value => ({
        ...value,
        income: value.investmentOption === InvestmentOptions.SelfSelect ? parsePercent(value.income) : undefined,
        inflation: value.investmentOption === InvestmentOptions.SelfSelect ? parsePercent(value.inflation) : undefined,
        growth: value.investmentOption === InvestmentOptions.SelfSelect ? parsePercent(value.growth) : undefined
      }))
    };

    const originalAllocations = primaryAllocations.map(value => ({
      ...value,
      income: value.investmentOption === InvestmentOptions.SelfSelect ? parsePercent(value.income) : undefined,
      inflation: value.investmentOption === InvestmentOptions.SelfSelect ? parsePercent(value.inflation) : undefined,
      growth: value.investmentOption === InvestmentOptions.SelfSelect ? parsePercent(value.growth) : undefined
    }));

    const isDirty = originalAllocations.some(allocation => !isEqual(_values.applicationAssetAllocations?.find(a => a.applicationId === allocation.applicationId), allocation));

    if (_values.confirmationType === ConfirmationType.SuggestAlternative && !isDirty) {
      setFormMode(FormMode.AlertNoChanges);
      return;
    }

    dispatch(confirmClientAsync(_values));
    setFormMode(FormMode.Confirming);
  }

  useEffect(() => {
    formMode === FormMode.Confirming && !isSubmitting && setFormMode(!errors ? FormMode.Confirmed : FormMode.None);
  }, [isSubmitting, errors]);

  useEffect(() => {
    dispatch(getClientAsync(clientId));
    dispatch(getAsync(clientId));
  }, [clientId]);

  return !client ? null : <>
    {!applicationNeedsConfirmation(client) ? <AlertMessage message="Application is already confirmed." /> : <>
      <FormikWrapper
        onSubmit={handleSubmit}
        validationSchema={confirmClientSchema}
        initialValues={{ ...confirmClient, clientId, confirmationType: undefined, confirmationComment: '', applicationAssetAllocations: primaryAllocations, incorrectPercent: undefined }}>
        {({ setFieldValue, values, errors }) => <>
          <AlertMessage message="Advice confirmation required" error hideClose></AlertMessage>
          <Box px={11} py={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Section hideBottomBorder hideTopBorder>
                  <TitleText hideMarginBottom>Advice confirmation</TitleText>
                </Section>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                <Section hideTopBorder>
                  <Typography variant="h5" gutterBottom>Application details</Typography>
                </Section>
                <Section hideTopBorder>
                  <Details title="Applicant">
                    <DetailText text={`${client.firstName} ${client.lastName}`} />
                  </Details>
                  <Details title="Email">
                    <DetailText text={client.email} />
                  </Details>
                  <Details title="Phone">
                    <DetailText text={client.mobile} />
                  </Details>
                </Section>
                <Section hideTopBorder>
                  <Details title="Adviser">
                    <DetailText text={client.adviserName} />
                  </Details>
                  <Details title="Application sent">
                    <DetailText text={createdDate} />
                  </Details>
                  <Details title="Application completed">
                    <DetailText text={completedDate} />
                  </Details>
                </Section>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Section hideTopBorder hideBottomBorder>
                  <Box display="flex" alignItems="center">
                    <Typography variant="h6" style={{ flexGrow: 1 }}>Savings Advice Tool results</Typography>
                    <Button variant="contained" color="primary" onClick={() => dispatch(getSavingsAdviceToolPdf(clientId))}>View results</Button>
                  </Box>
                </Section>
                <Section hideTopBorder>
                  <Box display="flex" alignItems="center">
                    <Typography variant="h6" style={{ flexGrow: 1 }}>Application</Typography>
                    <Button variant="contained" color="primary" onClick={() => dispatch(getApplicationPdf(clientId))}>View application</Button>
                  </Box>
                </Section>
                <Section hideTopBorder hideBottomBorder>
                  <Typography variant="h5">{client.firstName}'s asset allocation selection</Typography>
                </Section>
                {!!ksAllocations &&
                  <Section hideTopBorder>
                    <AssetAllocation clientName={client.firstName} allocation={ksAllocations} questionnaireResponses={responses} product={Product.KiwiSaver} />
                  </Section>
                }
                {wbAllocations.map((allocation, index) =>
                  <Section hideTopBorder key={index}>
                    <AssetAllocation clientName={client.firstName} allocation={allocation} questionnaireResponses={responses} product={Product.WealthBuilder} />
                  </Section>
                )}
                {incomeGeneratorApplications.map((application, index) =>
                  <Section hideTopBorder key={index}>
                    <Box display="flex" style={{ marginBottom: 10 }}>
                      <DetailText text="Income Generator" />
                      <Typography variant="subtitle1">{application.nickname || ''}</Typography>
                      {application.isJoint && <JointAccountIcon />}
                    </Box>
                    <Box display="flex">
                      <DetailText text="SAT result: " />
                      <Typography variant="subtitle1">N/A</Typography>
                    </Box>
                    <Box display="flex">
                      <DetailText text={`${client?.firstName}'s selection: `} />
                      <Typography variant="subtitle1">{application.active ? `${application.distributionOption === DistributionOptions.PaidOut ? 'Pay out' : 'Reinvest'} distributions` : 'Did not apply'}</Typography>
                    </Box>
                  </Section>
                )}
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Section hideBottomBorder hideTopBorder>
                  <FormLabel label="I'd like to" />
                  <RadioList
                    name="confirmationType"
                    value={values.confirmationType}
                    onChange={() => setFieldValue('applicationAssetAllocations', primaryAllocations)}
                    options={[
                      { value: ConfirmationType.Confirmed, label: 'Confirm the client\'s selection is based on my advice and proceed with the application' },
                      { value: ConfirmationType.SuggestAlternative, label: 'Confirm I have reviewed the client\'s selection and would like to suggest an alternative asset allocation' }
                    ]}
                  />
                </Section>
              </Grid>
            </Grid>
            {values.confirmationType === ConfirmationType.SuggestAlternative &&
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FieldArray name="applicationAssetAllocations" render={_ => {
                    return values.applicationAssetAllocations?.map((allocation: ExtendedApplicationAssetAllocation, index) => {
                      const totalPercent = (values: any) => FUNDS.reduce((sum, fund) => sum + (Number(values[fund.key]) || 0), 0)

                      return <Section hideBottomBorder hideTopBorder key={index}>
                        <div style={{ display: 'flex' }}>
                          <DetailText text={allocation.product === Product.IncomeGenerator ? 'Income Generator' : allocation.product === Product.WealthBuilder ? 'Wealth Builder' : 'Kiwisaver' || ''} />
                          <Typography variant="subtitle1">{`${allocation.nickname || ''}`}</Typography>
                          {allocation.isJoint && <JointAccountIcon />}
                        </div>
                        {allocation.product == Product.IncomeGenerator ?
                          <>
                            <Typography style={{ paddingBottom: 5 }}>Suggest alternative recommendation: </Typography>
                            <RadioList
                              name={`applicationAssetAllocations.${index}.distributionOption`}
                              value={allocation.distributionOption}
                              options={[{ value: DistributionOptions.PaidOut, label: 'Paid out' }, { value: DistributionOptions.Reinvest, label: 'Reinvest' }]}
                            />
                          </> : <>
                            <Typography style={{ paddingBottom: 5 }}>Suggest alternative asset allocation recommendation: </Typography>
                            <RadioList
                              name={`applicationAssetAllocations.${index}.investmentOption`}
                              value={allocation.investmentOption}
                              onChange={() => {
                                setFieldValue(`applicationAssetAllocations.${index}.income`, primaryAllocations[index].income);
                                setFieldValue(`applicationAssetAllocations.${index}.inflation`, primaryAllocations[index].inflation);
                                setFieldValue(`applicationAssetAllocations.${index}.growth`, primaryAllocations[index].growth);
                              }}
                              options={investmentOptionOptions({ kiwiSaver: allocation.product === Product.KiwiSaver, showBalancedOption: allocation.product === Product.KiwiSaver })}
                            />
                          </>
                        }
                        {allocation?.investmentOption === InvestmentOptions.SelfSelect &&
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell className={classes.tableCell}>
                                  <Typography className={classes.textWithHelpIcon}>Income Strategy</Typography>
                                </TableCell>
                                <TableCell className={clsx(classes.tableCell, classes.percentCell)}>
                                  <NumberTextInput
                                    name={`applicationAssetAllocations.${index}.income`}
                                    mask="999"
                                    placeholder="0"
                                    className={classes.percentInput}
                                    endAdornment="%"
                                  />
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className={classes.tableCell}>
                                  <Typography className={classes.textWithHelpIcon}>Inflation Strategy</Typography>
                                </TableCell>
                                <TableCell className={clsx(classes.tableCell, classes.percentCell)}>
                                  <NumberTextInput
                                    name={`applicationAssetAllocations.${index}.inflation`}
                                    mask="999"
                                    placeholder="0"
                                    className={classes.percentInput}
                                    endAdornment="%"
                                  />
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className={classes.tableCell}>
                                  <Typography className={classes.textWithHelpIcon}>Growth Strategy</Typography>
                                </TableCell>
                                <TableCell className={clsx(classes.tableCell, classes.percentCell)}>
                                  <NumberTextInput
                                    name={`applicationAssetAllocations.${index}.growth`}
                                    mask="999"
                                    placeholder="0"
                                    className={classes.percentInput}
                                    endAdornment="%"
                                  />
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className={classes.tableCell}>
                                  <Typography><b>Total</b></Typography>
                                </TableCell>
                                <TableCell className={clsx(classes.tableCell, classes.percentCell)}>
                                  <Box display="flex" justifyContent="flex-end">
                                    <Typography><b>{totalPercent(allocation)}%</b></Typography>
                                    {totalPercent(allocation) === 100 ? <CheckCircleIcon style={{ color: Colors.Green, marginLeft: 5 }} /> : <CancelIcon style={{ color: Colors.Red, marginLeft: 5 }} />}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        }
                      </Section>
                    })
                  }
                  } />
                </Grid>
                <Grid item xs={12} style={{ textAlign: 'right' }}>
                  <Section hideBottomBorder hideTopBorder>
                    <Button variant="contained" type="button" endIcon={<RefreshOutlinedIcon fontSize="small" />} onClick={() => setFieldValue('applicationAssetAllocations', primaryAllocations)}>Reset</Button>
                  </Section>
                </Grid>
              </Grid>
            }
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Section hideBottomBorder hideTopBorder>
                  <TextAreaInput name="confirmationComment" label="Add a custom message to your client" minRows={6} />
                </Section>
              </Grid>
            </Grid>
            <Grid container spacing={3} justifyContent="flex-end">
              <Grid item style={{ marginBottom: 10, paddingRight: 32 }}>
                <ErrorMessage name="incorrectPercent" />
              </Grid>
            </Grid>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item style={{ marginBottom: 10, paddingRight: 32 }}>
                <Button variant="contained" color="primary" type="submit">Submit</Button>
              </Grid>
            </Grid>
          </Box>
        </>}
      </FormikWrapper>
    </>}
    <DialogBox
      open={formMode === FormMode.Confirmed}
      icon={<CheckCircleIcon htmlColor={Colors.Green} />}
      ok={() => history.push(`/adviser/${clientId}/client-application`)}>
      <SubtitleText black hideMarginBottom>Application has been confirmed</SubtitleText>
    </DialogBox>
    <DialogBox
      open={formMode === FormMode.AlertNoChanges}
      icon={<CancelIcon htmlColor={Colors.Red} />}
      ok={() => setFormMode(FormMode.None)}>
      <SubtitleText black hideMarginBottom>Please provide an alternative suggestion or approve the application.</SubtitleText>
    </DialogBox>
  </>
}

const getAssetAllocationText = (allocation: ExtendedApplicationAssetAllocation) => {
  if (!allocation.active) return 'Did not apply';
  if (allocation.investmentOption === InvestmentOptions.SelfSelect) return <>Income: {allocation.income ?? 0}%&emsp;Inflation: {allocation.inflation ?? 0}%&emsp;Growth: {allocation.growth ?? 0}%</>;
  return `${allocation.investmentOption ? investmentOptionNames[allocation.investmentOption] : 'No'} asset allocation`;
}

const AssetAllocation = ({ clientName, allocation, questionnaireResponses, product }: {
  clientName: string | undefined;
  allocation: ExtendedApplicationAssetAllocation;
  questionnaireResponses?: any;
  product: Product;
}) => {
  const getSatRecommendedAllocation = () => {
    if (!questionnaireResponses) return '';
    const response = questionnaireResponses[`${product === Product.KiwiSaver ? 'Asset' : 'Wealth Builder asset'} allocation`];
    return response ? <>Income: {response.split(',')[0]}%&emsp;Inflation: {response.split(',')[1]}%&emsp;Growth: {response.split(',')[2]}%</> : '';
  }

  return <>
    <Box display="flex" mb={1}>
      <DetailText text={productDetails[product].name || ''} />
      <Typography variant="subtitle1">{allocation.nickname || ''}</Typography>
      {allocation.isJoint && <JointAccountIcon />}
    </Box>
    <Box display="flex">
      <DetailText text="SAT result: " />
      <Typography variant="subtitle1" style={{ flexGrow: 1 }}>{getSatRecommendedAllocation() || 'Life Cycle asset allocation'}</Typography>
    </Box>
    <Box display="flex">
      <DetailText text={`${clientName}'s selection: `} />
      <Typography variant="subtitle1">{getAssetAllocationText(allocation)}</Typography>
    </Box>
  </>
};

const JointAccountIcon = () => <PeopleAltRoundedIcon htmlColor={Colors.DarkGrey} fontSize="small" style={{ margin: 'auto 5px' }} />

const parsePercent = (value: number | string | undefined) => {
  if (!value) return 0;
  const number = Number(value);
  if (!number || number < 0 || number >= 101) return 0;
  return number;
}

const confirmClientSchema: yup.SchemaOf<ConfirmClientCommand> = yup.object({
  clientId: yup.string(),
  confirmationComment: yup.string(),
  confirmationType: yup.mixed<ConfirmationType>().required('An option is required'),
  applicationAssetAllocations: yup.array().of(yup.object({
    applicationId: yup.string(),
    investmentOption: yup.mixed<InvestmentOptions>(),
    growth: yup.number().default(0),
    inflation: yup.number().default(0),
    income: yup.number().default(0)
  }).test('100%', 'Total should be 100%', function ({ income, inflation, growth, investmentOption }) {
    return investmentOption !== InvestmentOptions.SelfSelect || (income + inflation + growth) === 100 || this?.createError({ path: `incorrectPercent`, message: 'Total must be 100% for Self Select asset allocations' });
  }))
});

const useStyles = makeStyles({
  tableCell: {
    padding: '4px 0'
  },
  percentCell: {
    width: 114,
    textAlign: 'right',
    paddingRight: 5
  },
  percentInput: {
    '& .MuiTypography-root': {
      color: Colors.Black
    },
    '& input': {
      textAlign: 'right'
    },
    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none'
    },
    '& input[type=number]': {
      '-moz-appearance': 'textfield'
    },
    '& .MuiInputAdornment-root .MuiTypography-root': {
      position: 'relative',
      left: -8,
      pointerEvents: 'none'
    }
  },
  textWithHelpIcon: {
    display: 'flex',
    alignItems: 'center'
  }
}, { name: 'AdviserConfirmation' });

export default AdviceConfirmation;
